import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import { io } from "socket.io-client";
import { supportApi } from "../../api/supportApi";
import {
  formatChatDateTime,
  formatConversationTimestamp,
} from "../../utils/chatDateTime";
import "./SupportChat.css";

const QUESTIONS = [
  { id: "store_setup", icon: "🛍️", title: "Setting up my store" },
  { id: "domain", icon: "🌐", title: "Choosing or connecting a domain" },
  { id: "payments", icon: "💳", title: "Payments and billing" },
  { id: "account", icon: "🔐", title: "Account access" },
  { id: "products", icon: "📦", title: "Products and orders" },
  { id: "other", icon: "💬", title: "Something else" },
];

const getInitialName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts.shift() || "", surname: parts.join(" ") };
};

const statusLabel = (status = "open") =>
  status.replace("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());

const getTicketActivityTime = (ticket) =>
  ticket.LastMessageAt ||
  ticket.Messages?.at(-1)?.CreatedAt ||
  ticket.UpdatedAt ||
  ticket.CreatedAt;

const getTimestamp = (value) => {
  const timestamp = Date.parse(value || "");
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const sortTicketsByLatestMessage = (items = []) =>
  [...items].sort(
    (first, second) =>
      getTimestamp(getTicketActivityTime(second)) -
      getTimestamp(getTicketActivityTime(first)),
  );

const GUEST_TICKETS_STORAGE_KEY = "toora.support.guestTickets";
const ACTIVE_GUEST_TICKET_STORAGE_KEY = "toora.support.activeGuestTicket";
const READ_MESSAGES_STORAGE_KEY = "toora.support.readMessages";

const readGuestTickets = () => {
  try {
    const storedTickets = JSON.parse(
      window.localStorage.getItem(GUEST_TICKETS_STORAGE_KEY) || "[]",
    );
    return Array.isArray(storedTickets) ? storedTickets : [];
  } catch (error) {
    console.warn("Could not restore saved support conversations.", error);
    return [];
  }
};

const readMessageReceipts = () => {
  try {
    const receipts = JSON.parse(
      window.localStorage.getItem(READ_MESSAGES_STORAGE_KEY) || "{}",
    );
    return receipts && typeof receipts === "object" ? receipts : {};
  } catch (error) {
    console.warn("Could not restore support notification state.", error);
    return {};
  }
};

const readActiveGuestTicketId = () => {
  try {
    return window.localStorage.getItem(ACTIVE_GUEST_TICKET_STORAGE_KEY) || "";
  } catch (error) {
    console.warn("Could not restore the active support conversation.", error);
    return "";
  }
};

const clearSavedActiveGuestTicket = () => {
  try {
    window.localStorage.removeItem(ACTIVE_GUEST_TICKET_STORAGE_KEY);
  } catch (error) {
    console.warn("Could not clear the saved support conversation.", error);
  }
};

const SupportChat = ({
  publicMode = false,
  popupMode = false,
  isWidgetOpen = false,
  onUnreadCount,
  onClose,
}) => {
  const { user } = useAuth();
  const { socket } = useNotifications();
  const isSignedInClient = String(user?.Role || "").toLowerCase() === "client";
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [guestTicketKeys, setGuestTicketKeys] = useState(readGuestTickets);
  const [readMessageIds, setReadMessageIds] = useState(readMessageReceipts);
  const [identity, setIdentity] = useState(() => getInitialName(user?.FullName));
  const [guestEmail, setGuestEmail] = useState(user?.Email || "");
  const [phone, setPhone] = useState(user?.MobileNumber || "");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [draft, setDraft] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [chatStage, setChatStage] = useState("topic");
  const [isStartingConversation, setIsStartingConversation] = useState(true);
  const messagesEndRef = useRef(null);
  const processedTicketLinkRef = useRef(false);
  const choseNewConversationRef = useRef(false);

  const publicTicketMap = useMemo(
    () =>
      new Map(
        guestTicketKeys.map((ticket) => [ticket.SupportTicketID, ticket]),
      ),
    [guestTicketKeys],
  );

  const markTicketRead = useCallback((ticketId, messageId) => {
    if (!ticketId || !messageId || readMessageIds[ticketId] === messageId) {
      return;
    }
    const nextReceipts = { ...readMessageIds, [ticketId]: messageId };
    setReadMessageIds(nextReceipts);
    try {
      window.localStorage.setItem(
        READ_MESSAGES_STORAGE_KEY,
        JSON.stringify(nextReceipts),
      );
    } catch (error) {
      console.warn("Could not save support notification state.", error);
    }
  }, [readMessageIds]);

  const removeGuestTicketKey = useCallback((ticketId) => {
    const nextTickets = guestTicketKeys.filter(
      (ticket) => ticket.SupportTicketID !== ticketId,
    );
    setGuestTicketKeys(nextTickets);
    try {
      window.localStorage.setItem(
        GUEST_TICKETS_STORAGE_KEY,
        JSON.stringify(nextTickets),
      );
    } catch (storageError) {
      console.warn("Could not remove expired support conversation access.", storageError);
    }
    if (activeTicket?.SupportTicketID === ticketId) {
      setActiveTicket(null);
      setMessages([]);
      setIsStartingConversation(false);
    }
    clearSavedActiveGuestTicket();
    setNotice("This chat has expired. Please start a new conversation.");
  }, [activeTicket?.SupportTicketID, guestTicketKeys]);

  const refreshActiveTicket = useCallback(async () => {
    if (!activeTicket?.SupportTicketID) return;
    try {
      const isGuestTicket = publicTicketMap.has(activeTicket.SupportTicketID);
      const response = isSignedInClient && !isGuestTicket
        ? await supportApi.getClientTicket(activeTicket.SupportTicketID)
        : await supportApi.getPublicTicket(
            activeTicket.SupportTicketID,
            publicTicketMap.get(activeTicket.SupportTicketID)?.accessToken,
          );
      setActiveTicket(response.data);
      setMessages(response.data.Messages || []);
      setIsStartingConversation(false);
      if (isWidgetOpen) {
        markTicketRead(
          response.data.SupportTicketID,
          response.data.Messages?.at(-1)?.SupportTicketMessageID,
        );
      }
    } catch (requestError) {
      if (
        publicTicketMap.has(activeTicket.SupportTicketID) &&
        /expired|not found/i.test(requestError.response?.data?.error || "")
      ) {
        removeGuestTicketKey(activeTicket.SupportTicketID);
        return;
      }
      setError(
        requestError.response?.data?.error ||
          "Could not refresh this support conversation.",
      );
    }
  }, [
    activeTicket?.ClientID,
    activeTicket?.SupportTicketID,
    isSignedInClient,
    isWidgetOpen,
    markTicketRead,
    publicTicketMap,
    removeGuestTicketKey,
  ]);

  const loadTickets = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      if (isSignedInClient) {
        const response = await supportApi.getClientTickets();
        const clientTickets = sortTicketsByLatestMessage(response.data || []);
        setTickets(clientTickets);
        if (!isWidgetOpen && onUnreadCount) {
          onUnreadCount(
            clientTickets.filter(
              (ticket) =>
                ticket.LastSenderType === "admin" &&
                readMessageIds[ticket.SupportTicketID] !== ticket.LastMessageID,
            ).length,
          );
        }
      } else {
        const ticketResults = await Promise.all(
          guestTicketKeys.map(async (savedTicket) => {
            try {
              const response = await supportApi.getPublicTicket(
                savedTicket.SupportTicketID,
                savedTicket.accessToken,
              );
              return { ticket: response.data, savedTicket };
            } catch (requestError) {
              return { savedTicket, error: requestError };
            }
          }),
        );
        const invalidTickets = ticketResults
          .filter(({ error }) => error?.response?.status === 404)
          .map(({ savedTicket }) => savedTicket.SupportTicketID);
        const transientFailure = ticketResults.find(
          ({ error }) => error && error.response?.status !== 404,
        );
        const guestTickets = sortTicketsByLatestMessage(ticketResults
          .filter(({ ticket }) => ticket)
          .map(({ ticket }) => ticket));
        if (invalidTickets.length) {
          const remainingKeys = guestTicketKeys.filter(
            (ticket) => !invalidTickets.includes(ticket.SupportTicketID),
          );
          setGuestTicketKeys(remainingKeys);
          try {
            window.localStorage.setItem(
              GUEST_TICKETS_STORAGE_KEY,
              JSON.stringify(remainingKeys),
            );
          } catch (storageError) {
            console.warn("Could not remove expired support conversation access.", storageError);
          }
          if (invalidTickets.includes(readActiveGuestTicketId())) {
            clearSavedActiveGuestTicket();
            setNotice("This chat has expired. Please start a new conversation.");
          }
        }
        if (transientFailure) {
          setError(
            transientFailure.error.response?.data?.error ||
              "Could not load your saved support conversations.",
          );
        }
        setTickets(guestTickets);
        if (!isWidgetOpen && onUnreadCount) {
          onUnreadCount(
            guestTickets.filter(
              (ticket) =>
                ticket.LastSenderType === "admin" &&
                readMessageIds[ticket.SupportTicketID] !== ticket.LastMessageID,
            ).length,
          );
        }
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Support is temporarily unavailable. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    guestTicketKeys,
    isSignedInClient,
    isWidgetOpen,
    onUnreadCount,
    readMessageIds,
  ]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (
      !loading &&
      tickets.length > 0 &&
      !activeTicket &&
      !choseNewConversationRef.current
    ) {
      setIsStartingConversation(false);
    }
  }, [activeTicket, loading, tickets.length]);

  useEffect(() => {
    if (isSignedInClient || activeTicket || choseNewConversationRef.current) {
      return;
    }
    const activeTicketId = readActiveGuestTicketId();
    const restoredTicket = tickets.find(
      (ticket) => ticket.SupportTicketID === activeTicketId,
    );
    if (!restoredTicket) return;
    setActiveTicket(restoredTicket);
    setMessages(restoredTicket.Messages || []);
    setIsStartingConversation(false);
  }, [activeTicket, isSignedInClient, tickets]);

  useEffect(() => {
    if (
      isSignedInClient ||
      !activeTicket?.SupportTicketID ||
      !publicTicketMap.has(activeTicket.SupportTicketID)
    ) {
      return;
    }
    try {
      window.localStorage.setItem(
        ACTIVE_GUEST_TICKET_STORAGE_KEY,
        activeTicket.SupportTicketID,
      );
    } catch (storageError) {
      console.warn("Could not save the active support conversation.", storageError);
    }
  }, [activeTicket, isSignedInClient, publicTicketMap]);

  useEffect(() => {
    if (!socket) return undefined;
    const refreshConversation = ({ ticketId } = {}) => {
      const ticketToPromote = ticketId || activeTicket?.SupportTicketID;
      setTickets((currentTickets) =>
        sortTicketsByLatestMessage(
          currentTickets.map((ticket) =>
            ticket.SupportTicketID === ticketToPromote
              ? { ...ticket, LastMessageAt: new Date().toISOString() }
              : ticket,
          ),
        ),
      );
      loadTickets({ silent: true });
      if (ticketId && ticketId === activeTicket?.SupportTicketID) {
        refreshActiveTicket();
      }
    };
    socket.on("support:updated", refreshConversation);
    const handleDeletedTicket = ({ ticketId } = {}) => {
      if (ticketId && publicTicketMap.has(ticketId)) {
        removeGuestTicketKey(ticketId);
      }
    };
    socket.on("support:deleted", handleDeletedTicket);
    return () => {
      socket.off("support:updated", refreshConversation);
      socket.off("support:deleted", handleDeletedTicket);
    };
  }, [
    activeTicket?.SupportTicketID,
    loadTickets,
    publicTicketMap,
    refreshActiveTicket,
    removeGuestTicketKey,
    socket,
  ]);

  useEffect(() => {
    if (isSignedInClient || guestTicketKeys.length === 0) return undefined;
    const connections = guestTicketKeys.map((ticket) => {
      const guestSocket = io({
        auth: {
          guestTicketId: ticket.SupportTicketID,
          guestAccessToken: ticket.accessToken,
        },
        reconnection: true,
        reconnectionAttempts: 10,
      });
      guestSocket.on("support:updated", (event) => {
        if (event?.ticketId !== ticket.SupportTicketID) return;
        setTickets((currentTickets) =>
          sortTicketsByLatestMessage(
            currentTickets.map((currentTicket) =>
              currentTicket.SupportTicketID === ticket.SupportTicketID
                ? { ...currentTicket, LastMessageAt: new Date().toISOString() }
                : currentTicket,
            ),
          ),
        );
        loadTickets({ silent: true });
        if (event.ticketId === activeTicket?.SupportTicketID) {
          refreshActiveTicket();
        }
      });
      guestSocket.on("support:deleted", (event) => {
        if (event?.ticketId !== ticket.SupportTicketID) return;
        removeGuestTicketKey(ticket.SupportTicketID);
      });
      return guestSocket;
    });
    return () => connections.forEach((connection) => connection.disconnect());
  }, [
    activeTicket?.SupportTicketID,
    guestTicketKeys,
    isSignedInClient,
    loadTickets,
    refreshActiveTicket,
    removeGuestTicketKey,
  ]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === GUEST_TICKETS_STORAGE_KEY) {
        setGuestTicketKeys(readGuestTickets());
      } else if (event.key === READ_MESSAGES_STORAGE_KEY) {
        setReadMessageIds(readMessageReceipts());
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectTicket = async (ticket) => {
    setError("");
    setNotice("");
    try {
      const isGuestTicket = publicTicketMap.has(ticket.SupportTicketID);
      const response = isSignedInClient && !isGuestTicket
        ? await supportApi.getClientTicket(ticket.SupportTicketID)
        : await supportApi.getPublicTicket(
            ticket.SupportTicketID,
            publicTicketMap.get(ticket.SupportTicketID)?.accessToken,
          );
      setActiveTicket(response.data);
      setMessages(response.data.Messages || []);
      setIsStartingConversation(false);
      markTicketRead(
        response.data.SupportTicketID,
        response.data.Messages?.at(-1)?.SupportTicketMessageID,
      );
    } catch (requestError) {
      if (
        publicTicketMap.has(ticket.SupportTicketID) &&
        /expired|not found/i.test(requestError.response?.data?.error || "")
      ) {
        removeGuestTicketKey(ticket.SupportTicketID);
        return;
      }
      setError(
        requestError.response?.data?.error ||
          "Could not open this support conversation.",
      );
    }
  };

  const startNewConversation = () => {
    choseNewConversationRef.current = true;
    setActiveTicket(null);
    setMessages([]);
    setSelectedTopic(null);
    setDraft("");
    setConsentAccepted(false);
    setChatStage("topic");
    setError("");
    setNotice("");
    setIsStartingConversation(true);
    clearSavedActiveGuestTicket();
  };

  const saveGuestTicketKey = useCallback((ticket, accessToken) => {
    const nextTickets = [
      {
        SupportTicketID: ticket.SupportTicketID,
        TicketNumber: ticket.TicketNumber,
        Subject: ticket.Subject,
        accessToken,
      },
      ...guestTicketKeys.filter(
        (item) => item.SupportTicketID !== ticket.SupportTicketID,
      ),
    ];
    setGuestTicketKeys(nextTickets);
    try {
      window.localStorage.setItem(
        GUEST_TICKETS_STORAGE_KEY,
        JSON.stringify(nextTickets),
      );
    } catch (error) {
      console.warn("Could not save support conversation access.", error);
    }
  }, [guestTicketKeys]);

  useEffect(() => {
    if (processedTicketLinkRef.current) return;
    const pendingTicket = window.tooraPendingGuestTicket;
    const params = new URLSearchParams(window.location.search);
    const ticketId = pendingTicket?.ticket || params.get("ticket");
    const accessToken = pendingTicket?.key || params.get("key");
    if (!ticketId || !accessToken) return;
    processedTicketLinkRef.current = true;
    supportApi
      .getPublicTicket(ticketId, accessToken)
      .then((response) => {
        saveGuestTicketKey(response.data, accessToken);
        window.tooraPendingGuestTicket = null;
        setActiveTicket(response.data);
        setMessages(response.data.Messages || []);
        setIsStartingConversation(false);
        markTicketRead(
          response.data.SupportTicketID,
          response.data.Messages?.at(-1)?.SupportTicketMessageID,
        );
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((requestError) => {
        const message =
          requestError.response?.data?.error ||
          "This chat has expired. Please start a new conversation.";
        setError(message);
        if (/expired|not found/i.test(message)) {
          clearSavedActiveGuestTicket();
        }
      })
      .finally(() => {
        window.tooraPendingGuestTicket = null;
      });
  }, [saveGuestTicketKey]);

  const createConversation = async (event) => {
    event.preventDefault();
    if (!isSignedInClient && !consentAccepted) {
      setError("Please consent to the use of your contact details.");
      return;
    }
    if (!selectedTopic || !draft.trim()) {
      setError("Choose a topic and tell us how we can help.");
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        ...identity,
        email: guestEmail.trim(),
        phone: phone.trim(),
        consentAccepted,
        topic: selectedTopic.id,
        message: draft.trim(),
      };
      const response = isSignedInClient
        ? await supportApi.createClientTicket(payload)
        : await supportApi.createPublicTicket(payload);
      const { ticket, accessToken } = response.data;
      if (accessToken) saveGuestTicketKey(ticket, accessToken);
      setDraft("");
      setNotice(
        `Conversation started. Your reference is ${ticket.TicketNumber}.`,
      );
      const detail = isSignedInClient
        ? await supportApi.getClientTicket(ticket.SupportTicketID)
        : await supportApi.getPublicTicket(ticket.SupportTicketID, accessToken);
      setActiveTicket(detail.data);
      setMessages(detail.data.Messages || []);
      setIsStartingConversation(false);
      setTickets((currentTickets) => sortTicketsByLatestMessage([
        detail.data,
        ...currentTickets.filter(
          (item) => item.SupportTicketID !== detail.data.SupportTicketID,
        ),
      ]));
      setChatStage("topic");
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not start your support conversation.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!activeTicket || !reply.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const token = publicTicketMap.get(activeTicket.SupportTicketID)?.accessToken;
      if (
        isSignedInClient &&
        !publicTicketMap.has(activeTicket.SupportTicketID)
      ) {
        await supportApi.sendClientMessage(activeTicket.SupportTicketID, reply.trim());
      } else {
        await supportApi.sendPublicMessage(
          activeTicket.SupportTicketID,
          token,
          reply.trim(),
        );
      }
      const sentAt = new Date().toISOString();
      const senderType = isSignedInClient ? "client" : "visitor";
      setTickets((currentTickets) =>
        sortTicketsByLatestMessage(
          currentTickets.map((ticket) =>
            ticket.SupportTicketID === activeTicket.SupportTicketID
              ? {
                  ...ticket,
                  LastMessage: reply.trim(),
                  LastMessageAt: sentAt,
                  LastSenderType: senderType,
                }
              : ticket,
          ),
        ),
      );
      setReply("");
      await refreshActiveTicket();
      await loadTickets({ silent: true });
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not send your message. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const beginQuestion = (question) => {
    setSelectedTopic(question);
    setChatStage(isSignedInClient ? "message" : "identity");
    setError("");
    setIsStartingConversation(true);
  };

  const showConversationList = () => {
    setError("");
    setNotice("");
    setMessages([]);
    setActiveTicket(null);
    setIsStartingConversation(false);
    clearSavedActiveGuestTicket();
  };

  return (
    <section
      id={publicMode ? "support-chat" : undefined}
      className={`support-chat ${publicMode ? "support-chat-public" : ""} ${
        popupMode ? "support-chat-popup" : ""
      }`}
    >
      <header className="support-chat-page-header">
        <div>
          {!popupMode && <span className="support-chat-eyebrow">CUSTOMER CARE</span>}
          <h1>{popupMode || publicMode ? "Toora Flex Support" : "Communication"}</h1>
          <p>
            {publicMode
              ? "Choose what you need help with and our team will follow up."
              : "Your conversations with the Toora Flex team, all in one place."}
          </p>
        </div>
        <button type="button" className="support-refresh" onClick={loadTickets}>
          <i className="fas fa-rotate-right" /> Refresh
        </button>
        {onClose && (
          <button
            type="button"
            className="website-support-close"
            onClick={onClose}
            aria-label="Close support chat"
          >
            <i className="fas fa-times" />
          </button>
        )}
      </header>

      {error && <div className="support-chat-alert support-chat-error">{error}</div>}
      {notice && (
        <div className="support-chat-alert support-chat-notice">{notice}</div>
      )}

      <div className="support-chat-layout">
        <aside
          className={`support-ticket-sidebar ${
            activeTicket || isStartingConversation ? "mobile-hide" : ""
          }`}
        >
          <div className="support-ticket-sidebar-header">
            <div>
              <strong>Your conversations</strong>
              <span>{tickets.length} ticket{tickets.length === 1 ? "" : "s"}</span>
            </div>
            <button
              type="button"
              onClick={startNewConversation}
              aria-label="Start a new conversation"
            >
              <i className="fas fa-plus" />
            </button>
          </div>
          <div className="support-ticket-list">
            {loading ? (
              <div className="support-ticket-empty">Loading conversations…</div>
            ) : tickets.length === 0 ? (
              <div className="support-ticket-empty">
                <span className="support-empty-icon">💬</span>
                <strong>No conversations yet</strong>
                <span>Start a chat and we’ll help you out.</span>
              </div>
            ) : (
              tickets.map((ticket) => (
                <button
                  type="button"
                  key={ticket.SupportTicketID}
                  className={`support-ticket-row ${activeTicket?.SupportTicketID === ticket.SupportTicketID ? "selected" : ""}`}
                  onClick={() => selectTicket(ticket)}
                >
                  <span className="support-avatar">
                    {ticket.LastSenderType === "admin" ? "👩🏽‍💻" : "🙂"}
                  </span>
                  <span className="support-ticket-row-copy">
                    <span className="support-ticket-row-heading">
                      <span className="support-ticket-row-title">{ticket.Subject}</span>
                      <time
                        className="support-ticket-row-time"
                        dateTime={ticket.LastMessageAt || ticket.CreatedAt}
                        title={formatChatDateTime(ticket.LastMessageAt || ticket.CreatedAt)}
                      >
                        {formatConversationTimestamp(ticket.LastMessageAt || ticket.CreatedAt)}
                      </time>
                    </span>
                    <span className="support-ticket-row-preview">
                      {ticket.LastMessage || "Conversation started"}
                    </span>
                  </span>
                  <span className="support-ticket-row-meta">
                    {ticket.LastSenderType === "admin" && !isWidgetOpen && (
                      <small className="support-new-reply">New reply</small>
                    )}
                    <small className={`support-status status-${ticket.Status}`}>
                      {statusLabel(ticket.Status)}
                    </small>
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        <main
          className={`support-chat-panel ${
            !activeTicket && !isStartingConversation ? "mobile-hide" : ""
          }`}
        >
          {activeTicket ? (
            <>
              <div className="support-conversation-header">
                <button
                  type="button"
                  className="support-back-to-list"
                  onClick={showConversationList}
                  aria-label="Back to conversation list"
                  title="Back to conversations"
                >
                  <i className="fas fa-arrow-left" />
                  {popupMode && <span>Chats</span>}
                </button>
                <span className="support-avatar support-avatar-large">👩🏽‍💻</span>
                <div className="support-conversation-title">
                  <strong>Toora Flex Support</strong>
                  <span>
                    {activeTicket.TicketNumber} · {activeTicket.Subject}
                  </span>
                </div>
                <button
                  type="button"
                  className="support-new-conversation"
                  onClick={startNewConversation}
                  aria-label="Start a new conversation"
                  title="Start a new conversation"
                >
                  <i className="fas fa-plus" />
                </button>
                <span className={`support-status status-${activeTicket.Status}`}>
                  {statusLabel(activeTicket.Status)}
                </span>
              </div>
              <div className="support-chat-wallpaper" role="log" aria-live="polite">
                <div className="support-chat-welcome">
                  <span>👋</span>
                  <strong>Welcome to Toora Flex Support</strong>
                  <p>Messages and replies for this request stay together here.</p>
                </div>
                {messages.map((item) => (
                  <div
                    key={item.SupportTicketMessageID}
                    className={`support-message ${item.SenderType === "client" || item.SenderType === "visitor" ? "message-from-user" : "message-from-team"} ${item.SenderType === "system" ? "message-system" : ""}`}
                  >
                    {item.SenderType !== "client" && item.SenderType !== "visitor" && (
                      <span className="support-message-avatar">
                        {item.SenderType === "system" ? "✨" : "👩🏽‍💻"}
                      </span>
                    )}
                    <div className="support-message-bubble">
                      <span className="support-message-sender">
                        {item.SenderType === "admin" ? "Toora Flex Team" : item.SenderName}
                      </span>
                      <p>{item.Message}</p>
                      <time>{formatChatDateTime(item.CreatedAt)}</time>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              {activeTicket.Status === "closed" || activeTicket.Status === "resolved" ? (
                <div className="support-conversation-ended">
                  <span>🔒</span>
                  This conversation has ended.{" "}
                  <button type="button" onClick={startNewConversation}>
                    Start a new support request
                  </button>
                </div>
              ) : (
                <form className="support-reply-form" onSubmit={sendReply}>
                  <label className="support-reply-emoji" aria-label="Chat">
                    💬
                  </label>
                  <textarea
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder="Write a message…"
                    rows={1}
                    maxLength={5000}
                  />
                  <button type="submit" disabled={!reply.trim() || submitting} aria-label="Send message">
                    <i className="fas fa-paper-plane" />
                  </button>
                </form>
              )}
            </>
          ) : (
            <div className="support-start-flow">
              <div className="support-start-greeting">
                <span className="support-avatar support-avatar-large">👋</span>
                <div>
                  <strong>Hi there! How can we help?</strong>
                  <p>Select a question to get started. A support ticket will be created for your conversation.</p>
                </div>
              </div>

              {chatStage === "topic" && (
                <div className="support-question-list">
                  {QUESTIONS.map((question) => (
                    <button type="button" key={question.id} onClick={() => beginQuestion(question)}>
                      <span>{question.icon}</span>
                      {question.title}
                      <i className="fas fa-chevron-right" />
                    </button>
                  ))}
                  {!isSignedInClient && (
                    <p className="support-signin-hint">
                      Already a client? <a href="/login">Sign in</a> to keep your conversations with your account.
                    </p>
                  )}
                </div>
              )}

              {chatStage !== "topic" && (
                <form className="support-start-form" onSubmit={createConversation}>
                  <div className="support-selected-question">
                    <span>{selectedTopic?.icon}</span>
                    <strong>{selectedTopic?.title}</strong>
                    <button type="button" onClick={() => setChatStage("topic")}>Change</button>
                  </div>
                  {!isSignedInClient && chatStage === "identity" && (
                    <>
                      <p>Before you chat, tell us who we’ll be replying to.</p>
                      <div className="support-identity-fields">
                        <label>
                          First name
                          <input required autoComplete="given-name" value={identity.firstName} onChange={(event) => setIdentity((current) => ({ ...current, firstName: event.target.value }))} />
                        </label>
                        <label>
                          Surname
                          <input required autoComplete="family-name" value={identity.surname} onChange={(event) => setIdentity((current) => ({ ...current, surname: event.target.value }))} />
                        </label>
                        <label className="support-field-wide">
                          Email
                          <input required type="email" autoComplete="email" value={guestEmail} onChange={(event) => setGuestEmail(event.target.value)} />
                        </label>
                        <label className="support-field-wide">
                          Phone (optional)
                          <input type="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} />
                        </label>
                      </div>
                      <label className="support-privacy-consent">
                        <input
                          type="checkbox"
                          checked={consentAccepted}
                          onChange={(event) =>
                            setConsentAccepted(event.target.checked)
                          }
                          required
                        />
                        <span>
                          I consent to Toora Flex using my contact details to
                          respond to this support request, in line with the{" "}
                          <a href="/privacy" target="_blank" rel="noreferrer">
                            Privacy Policy
                          </a>
                          .
                        </span>
                      </label>
                    </>
                  )}
                  <label className="support-message-label">
                    {chatStage === "identity" ? "What would you like help with?" : "Tell us a little more"}
                    <textarea
                      required
                      rows={4}
                      maxLength={5000}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      placeholder="Type your question here…"
                    />
                  </label>
                  <div className="support-start-actions">
                    <button type="button" className="support-secondary-button" onClick={() => setChatStage(isSignedInClient ? "topic" : "identity")}>
                      Back
                    </button>
                    <button type="submit" disabled={submitting}>
                      {submitting ? "Starting…" : "Start conversation"}
                      <i className="fas fa-paper-plane" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </main>
      </div>
    </section>
  );
};

export default SupportChat;
